import React from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

/* Outer row — on mobile this is a flex row containing tabs + budget pill */
const TabNav = styled.nav`
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 24px 24px 0 24px;
  flex-wrap: wrap;

  ${props => props.$isMobile && css`
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 0;
    padding: 12px 12px 8px 0;
    align-items: center;
    overflow: hidden;
  `}
`;

/* Scrollable tabs strip — only used on mobile */
const TabsScroll = styled.div`
  display: flex;
  gap: 10px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  padding: 0 12px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const BudgetPill = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: #eff6ff;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: #2563eb;
  white-space: nowrap;
  margin-right: 12px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #dbeafe;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
  }

  &:active {
    transform: scale(0.96);
  }
`;

const DayTab = styled(motion.button)`
  padding: 8px 32px;
  border-radius: 9999px;
  border: none;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#1f2937' : '#374151'};
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;
  box-shadow: ${props => props.$active
    ? '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'
    : 'none'};

  &:hover {
    background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.3)'};
  }

  /* Mobile overrides */
  ${props => props.$isMobile && css`
    padding: 6px 14px;
    font-size: 12px;
    background: ${props.$active ? 'black' : 'rgba(255, 255, 255, 0.5)'};
    color: ${props.$active ? 'white' : '#374151'};
    box-shadow: ${props.$active
      ? '0 2px 6px rgba(0, 0, 0, 0.15)'
      : 'none'};

    &:hover {
      background: ${props.$active ? 'black' : 'rgba(255, 255, 255, 0.6)'};
    }
  `}
`;

const VibeDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$color || 'transparent'};
  margin: 4px auto 0 auto;
  transition: background 0.3s;
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DayTabBar = ({ days, activeDay, onDayChange, isMobile, budget, onBudgetClick, vibeColors = [] }) => {
  const tabs = days.map((day, index) => {
    const dayNum = day.day || index + 1;
    const isToday = index === 0;
    const vibeColor = vibeColors[index];
    return (
      <DayTab
        key={dayNum}
        $active={activeDay === index}
        $isMobile={isMobile}
        onClick={() => onDayChange(index)}
        whileTap={{ scale: 0.95 }}
      >
        <TabContent>
          {isMobile && isToday ? 'Today' : `Day ${dayNum}${!isMobile && isToday ? ' (Today)' : ''}`}
          {vibeColor && <VibeDot $color={vibeColor} />}
        </TabContent>
      </DayTab>
    );
  });

  if (isMobile) {
    return (
      <TabNav $isMobile={isMobile}>
        <TabsScroll>{tabs}</TabsScroll>
        {budget && <BudgetPill onClick={onBudgetClick}>💰 {budget}</BudgetPill>}
      </TabNav>
    );
  }

  return (
    <TabNav>
      {tabs}
    </TabNav>
  );
};

export default DayTabBar;
